import { isAuthorized } from "domain/auth/authorizationPolicy";

import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { logEvent } from "firebase/analytics";
import {
  GoogleAuthProvider,
  User,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { analytics, auth } from "firebaseConfig";

export interface UserRoles {
  admin: boolean;
}

export const UserContext = createContext<UserContextType>({
  currentUser: null,
  roles: { admin: false },
  signIn: () => Promise.resolve(),
  signOutCallToAction: () => Promise.resolve(),
});

interface UserContextType {
  currentUser: User | null;
  roles: UserRoles;
  signIn: () => Promise<void>;
  signOutCallToAction: () => Promise<void>;
}

export const UserProvider = ({ children }: any) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const provider = new GoogleAuthProvider();

  useEffect(() => {
    auth.onAuthStateChanged(function (user) {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });
  }, []);

  const signIn = async () => {
    signInWithPopup(auth, provider)
      .then((result) => {
        const user = result.user;
        setCurrentUser(user);
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        const email = error.customData.email;
        const credential = GoogleAuthProvider.credentialFromError(error);

        throw new Error(errorCode + errorMessage + email + credential);
      });
  };
  const signOutCallToAction = async () => {
    signOut(auth)
      .then(() => {
        if (analytics) {
          logEvent(analytics, "logout", { user: currentUser?.email });
        }
        setCurrentUser(null);
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        const email = error.customData.email;
        const credential = GoogleAuthProvider.credentialFromError(error);

        throw new Error(errorCode + errorMessage + email + credential);
      });
  };

  const roles = useMemo<UserRoles>(
    () => ({ admin: isAuthorized(currentUser) }),
    [currentUser]
  );

  return (
    <UserContext.Provider value={{ currentUser, roles, signIn, signOutCallToAction }}>
      {children}
    </UserContext.Provider>
  );
};

export const useCurrentUser = () => useContext(UserContext);
